import { useEffect, useRef } from 'react'

// The Screen Wake Lock API is not in every TypeScript DOM lib we
// build against, so the small part we use is typed here
interface ScreenWakeLockSentinel {
  released: boolean
  release: () => Promise<void>
}

interface ScreenWakeLock {
  request: (type: 'screen') => Promise<ScreenWakeLockSentinel>
}

function getWakeLock(): ScreenWakeLock | null {
  const nav = navigator as unknown as {
    wakeLock?: ScreenWakeLock
  }
  return nav.wakeLock ?? null
}

/**
 * Asks the browser to keep the screen awake while `active` is true, so
 * a device does not dim or lock during something the user watches or
 * listens to rather than touches.
 *
 * ```
 * useScreenWakeLock(isPlaying)
 * ```
 *
 * Browsers drop the lock whenever the page stops being visible (tab
 * switch, screen locked by hand), so it is asked for again once the
 * page is visible. Where the API is missing — older iOS, or a page
 * served over plain HTTP — nothing happens and the screen behaves as
 * it normally would.
 *
 * @param active Whether the screen should be kept awake
 */
export function useScreenWakeLock(active: boolean) {
  const sentinelRef = useRef<ScreenWakeLockSentinel | null>(null)

  useEffect(() => {
    const wakeLock = getWakeLock()
    if (!active || !wakeLock) return

    let cancelled = false

    const acquire = async () => {
      // A lock the browser has already released is no use. That
      // happens when the page hides while a request is in flight,
      // so check rather than trust what we are holding
      const held = sentinelRef.current
      if (held && !held.released) return
      // A hidden page cannot hold a lock, wait for it to come back
      if (document.visibilityState !== 'visible') return
      try {
        const sentinel = await wakeLock.request('screen')
        if (cancelled) {
          void sentinel.release().catch(() => {})
          return
        }
        sentinelRef.current = sentinel
      } catch {
        // The request can be refused, for example on low battery.
        // Playing carries on without keeping the screen awake
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void acquire()
      } else {
        // The browser has already released it while hidden
        sentinelRef.current = null
      }
    }

    void acquire()
    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    )

    return () => {
      cancelled = true
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      )
      const sentinel = sentinelRef.current
      sentinelRef.current = null
      // Releasing can reject if the lock has already gone, which
      // makes no difference to us
      void sentinel?.release().catch(() => {})
    }
  }, [active])
}
